import { ref } from 'vue';
import { 
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  updateDoc,
  query,
  where,
  getDocs,
  deleteDoc,
  addDoc
} from 'firebase/firestore';
import { db } from '@/firebase';
import { logError } from '@/utils/secureLogger';

interface AvailabilitySlot {
  dayOfWeek: string;
  startTime: string;
  endTime: string;
  mode: 'online' | 'face-to-face' | 'both';
}

// Simple cache to avoid redundant queries
const availabilityCache = new Map<string, { data: AvailabilitySlot[], timestamp: number }>();
const CACHE_DURATION = 30000; // 30 seconds

export const useTutorAvailabilityStore = () => {
  const isLoading = ref(false);

  /**
   * Add a single availability slot to Firestore subcollection (more efficient)
   */
  const addSingleSlot = async (tutorId: string, slot: AvailabilitySlot) => {
    isLoading.value = true;
    
    try {
      const availabilityRef = collection(db, 'users', tutorId, 'availability');
      
      await addDoc(availabilityRef, {
        dayOfWeek: slot.dayOfWeek,
        startTime: slot.startTime,
        endTime: slot.endTime,
        mode: slot.mode,
        updatedAt: new Date(),
        createdAt: new Date()
      });

      // Invalidate cache after adding
      availabilityCache.delete(tutorId);

      return true;
      
    } catch (error) {
      logError('Error adding single availability slot', error instanceof Error ? error : new Error(String(error)));
      throw error;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Remove a single availability slot from Firestore subcollection
   * More efficient version that tries to find and delete quickly
   */
  const removeSingleSlot = async (tutorId: string, slot: AvailabilitySlot) => {
    isLoading.value = true;
    
    try {
      const availabilityRef = collection(db, 'users', tutorId, 'availability');
      
      // Create a query to find the specific document more efficiently
      const q = query(
        availabilityRef,
        where('dayOfWeek', '==', slot.dayOfWeek),
        where('startTime', '==', slot.startTime),
        where('endTime', '==', slot.endTime),
        where('mode', '==', slot.mode)
      );
      
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        // Delete the first matching document
        const docToDelete = querySnapshot.docs[0];
        await deleteDoc(doc(db, 'users', tutorId, 'availability', docToDelete.id));
        
        // Invalidate cache after removing
        availabilityCache.delete(tutorId);
        
        return true;
      } else {
        return false;
      }
      
    } catch (error) {
      logError('Error removing single availability slot', error instanceof Error ? error : new Error(String(error)));
      throw error;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Save tutor availability to Firestore subcollection
   * ⚠️ WARNING: This method is SLOW - it deletes ALL slots and re-adds ALL slots
   * Use addSingleSlot() or removeSingleSlot() for better performance
   * Only use this for bulk operations or initial setup
   */
  const saveAvailability = async (tutorId: string, slots: AvailabilitySlot[]) => {
    isLoading.value = true;
    
    try {
      // First clear existing availability slots in the subcollection
      const availabilityRef = collection(db, 'users', tutorId, 'availability');
      const existingSlots = await getDocs(availabilityRef);
      
      // Delete existing slots
      for (const docSnapshot of existingSlots.docs) {
        await deleteDoc(doc(db, 'users', tutorId, 'availability', docSnapshot.id));
      }
      
      // Add new slots to the subcollection
      for (const slot of slots) {
        await addDoc(availabilityRef, {
          dayOfWeek: slot.dayOfWeek,
          startTime: slot.startTime,
          endTime: slot.endTime,
          mode: slot.mode,
          updatedAt: new Date(),
          createdAt: new Date()
        });
      }

      return true;
      
    } catch (error) {
      logError('Error saving tutor availability', error instanceof Error ? error : new Error(String(error)));
      throw error;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Get tutor availability from Firestore subcollection
   */
  const getAvailability = async (tutorId: string, forceRefresh = false): Promise<AvailabilitySlot[]> => {
    // Check cache first (unless force refresh)
    if (!forceRefresh) {
      const cached = availabilityCache.get(tutorId);
      if (cached && (Date.now() - cached.timestamp) < CACHE_DURATION) {
        return cached.data;
      }
    }
    
    isLoading.value = true;
    
    try {
      const availabilityRef = collection(db, 'users', tutorId, 'availability');
      const querySnapshot = await getDocs(availabilityRef);
      
      const slots: AvailabilitySlot[] = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        slots.push({
          dayOfWeek: data.dayOfWeek,
          startTime: data.startTime,
          endTime: data.endTime,
          mode: data.mode
        });
      });
      
      // Update cache
      availabilityCache.set(tutorId, {
        data: slots,
        timestamp: Date.now()
      });
      
      return slots;
      
    } catch (error) {
      logError('Error getting tutor availability', error instanceof Error ? error : new Error(String(error)));
      throw error;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Get available time slots for a specific tutor and day
   */
  const getAvailableSlots = async (
    tutorId: string, 
    dayOfWeek: string
  ): Promise<AvailabilitySlot[]> => {
    try {
      const allSlots = await getAvailability(tutorId);
      
      return allSlots.filter(slot => slot.dayOfWeek === dayOfWeek);
      
    } catch (error) {
      logError('Error getting available slots', error instanceof Error ? error : new Error(String(error)));
      throw error;
    }
  };

  /**
   * Check if tutor is available at specific time
   */
  const isAvailableAt = async (
    tutorId: string,
    dayOfWeek: string,
    startTime: string,
    endTime: string,
    mode: 'online' | 'face-to-face'
  ): Promise<boolean> => {
    try {
      const slots = await getAvailableSlots(tutorId, dayOfWeek);
      
      return slots.some(slot => {
        const slotStart = slot.startTime;
        const slotEnd = slot.endTime;
        const modeMatches = slot.mode === 'both' || slot.mode === mode;
        
        // Check if requested time falls within available slot
        const timeOverlap = startTime >= slotStart && endTime <= slotEnd;
        
        return timeOverlap && modeMatches;
      });
      
    } catch (error) {
      logError('Error checking availability', error instanceof Error ? error : new Error(String(error)));
      return false;
    }
  };

  /**
   * Get all available tutors for a specific time slot
   */
  const getAvailableTutors = async (
    dayOfWeek: string,
    startTime: string,
    endTime: string,
    mode: 'online' | 'face-to-face'
  ): Promise<string[]> => {
    try {
      // First get all tutors
      const tutorsQuery = query(
        collection(db, 'users'),
        where('userType', '==', 'tutor'),
        where('isVerified', '==', true)
      );
      const tutorsSnapshot = await getDocs(tutorsQuery);
      
      const availableTutors: string[] = [];
      
      // Check each tutor's availability subcollection
      for (const tutorDoc of tutorsSnapshot.docs) {
        const tutorId = tutorDoc.id;
        
        try {
          const availabilityRef = collection(db, 'users', tutorId, 'availability');
          const availabilitySnapshot = await getDocs(availabilityRef);
          
          const isAvailable = availabilitySnapshot.docs.some(doc => {
            const slot = doc.data();
            const slotStart = slot.startTime;
            const slotEnd = slot.endTime;
            const dayMatches = slot.dayOfWeek === dayOfWeek;
            const modeMatches = slot.mode === 'both' || slot.mode === mode;
            const timeOverlap = startTime >= slotStart && endTime <= slotEnd;
            
            return dayMatches && modeMatches && timeOverlap;
          });
          
          if (isAvailable) {
            availableTutors.push(tutorId);
          }
        } catch (error) {
          logError('Error checking availability for tutor', error instanceof Error ? error : new Error(String(error)));
        }
      }
      
      return availableTutors;
      
    } catch (error) {
      logError('Error getting available tutors', error instanceof Error ? error : new Error(String(error)));
      throw error;
    }
  };

  /**
   * Update a specific availability slot in subcollection
   */
  const updateAvailabilitySlot = async (
    tutorId: string,
    oldSlot: AvailabilitySlot,
    newSlot: AvailabilitySlot
  ) => {
    try {
      // Get all current slots
      const allSlots = await getAvailability(tutorId);
      
      // Find and update the specific slot
      const updatedSlots = allSlots.map(slot => {
        if (slot.dayOfWeek === oldSlot.dayOfWeek && 
            slot.startTime === oldSlot.startTime &&
            slot.endTime === oldSlot.endTime &&
            slot.mode === oldSlot.mode) {
          return newSlot;
        }
        return slot;
      });
      
      // Save the updated slots
      await saveAvailability(tutorId, updatedSlots);
      
    } catch (error) {
      logError('Error updating availability slot', error instanceof Error ? error : new Error(String(error)));
      throw error;
    }
  };

  /**
   * Remove a specific availability slot from subcollection
   */
  const removeAvailabilitySlot = async (
    tutorId: string,
    slotToRemove: AvailabilitySlot
  ) => {
    try {
      const allSlots = await getAvailability(tutorId);
      
      const updatedSlots = allSlots.filter(slot => 
        !(slot.dayOfWeek === slotToRemove.dayOfWeek && 
          slot.startTime === slotToRemove.startTime &&
          slot.endTime === slotToRemove.endTime &&
          slot.mode === slotToRemove.mode)
      );
      
      await saveAvailability(tutorId, updatedSlots);
      
    } catch (error) {
      logError('Error removing availability slot', error instanceof Error ? error : new Error(String(error)));
      throw error;
    }
  };

  /**
   * Clear all availability for a tutor
   */
  const clearAvailability = async (tutorId: string) => {
    try {
      await saveAvailability(tutorId, []);
      
    } catch (error) {
      logError('Error clearing availability', error instanceof Error ? error : new Error(String(error)));
      throw error;
    }
  };

  /**
   * Get tutors available for a specific date and time
   * This is useful for the booking system
   */
  const getTutorsForDateTime = async (
    date: Date,
    startTime: string,
    endTime: string,
    mode: 'online' | 'face-to-face'
  ): Promise<string[]> => {
    try {
      const dayOfWeek = date.toLocaleDateString('en-US', { weekday: 'long' });
      
      return await getAvailableTutors(dayOfWeek, startTime, endTime, mode);
      
    } catch (error) {
      logError('Error getting tutors for date/time', error instanceof Error ? error : new Error(String(error)));
      throw error;
    }
  };

  /**
   * Validate if a time slot is valid (no conflicts, proper format)
   */
  const validateTimeSlot = (slot: AvailabilitySlot): { isValid: boolean; error?: string } => {
    // Check if start time is before end time
    if (slot.startTime >= slot.endTime) {
      return {
        isValid: false,
        error: 'Start time must be before end time'
      };
    }

    // Check if times are in valid format (HH:MM)
    const timeRegex = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;
    if (!timeRegex.test(slot.startTime) || !timeRegex.test(slot.endTime)) {
      return {
        isValid: false,
        error: 'Invalid time format. Please use HH:MM format'
      };
    }

    // Check if day of week is valid
    const validDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    if (!validDays.includes(slot.dayOfWeek)) {
      return {
        isValid: false,
        error: 'Invalid day of week'
      };
    }

    // Check if mode is valid
    const validModes = ['online', 'face-to-face', 'both'];
    if (!validModes.includes(slot.mode)) {
      return {
        isValid: false,
        error: 'Invalid mode. Must be online, face-to-face, or both'
      };
    }

    return { isValid: true };
  };

  /**
   * Check for overlapping time slots for the same day
   */
  const checkForOverlaps = (slots: AvailabilitySlot[]): { hasOverlaps: boolean; conflicts?: string[] } => {
    const conflicts: string[] = [];
    
    for (let i = 0; i < slots.length; i++) {
      for (let j = i + 1; j < slots.length; j++) {
        const slot1 = slots[i];
        const slot2 = slots[j];
        
        // Check if slots are for the same day
        if (slot1.dayOfWeek === slot2.dayOfWeek) {
          // Check if times overlap
          const start1 = slot1.startTime;
          const end1 = slot1.endTime;
          const start2 = slot2.startTime;
          const end2 = slot2.endTime;
          
          if ((start1 < end2 && end1 > start2)) {
            conflicts.push(`${slot1.dayOfWeek}: ${start1}-${end1} overlaps with ${start2}-${end2}`);
          }
        }
      }
    }
    
    return {
      hasOverlaps: conflicts.length > 0,
      conflicts: conflicts.length > 0 ? conflicts : undefined
    };
  };

  return {
    isLoading,
    saveAvailability,
    addSingleSlot,
    removeSingleSlot,
    getAvailability,
    getAvailableSlots,
    isAvailableAt,
    getAvailableTutors,
    updateAvailabilitySlot,
    removeAvailabilitySlot,
    clearAvailability,
    getTutorsForDateTime,
    validateTimeSlot,
    checkForOverlaps
  };
};